/**
 * App entry point.
 *
 * Mounts the React 19 root with:
 *   - `AuthProvider` (Firebase auth state + Microsoft flows)
 *   - `RouterProvider` fed the live auth context (InnerApp pattern below), so the
 *     router's `beforeLoad` guards always see the current user
 *   - the sonner `Toaster`
 *
 * The router instance is created once. On every auth change we (a) re-inject the
 * fresh context via the `RouterProvider context` prop and (b) invalidate matches
 * so guards re-run (e.g. redirect to /login on sign-out, into the app on sign-in).
 */
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'

import { ThemeProvider } from '@/components/theme-provider'
import { Toaster } from '@/components/ui/sonner'
import { AuthProvider, useAuth } from '@/lib/auth'
import { type AppRouter, createAppRouter } from '@/router'
import './index.css'

function InnerApp() {
  const auth = useAuth()
  // Create the router exactly once, seeded with the initial auth context.
  const [router] = useState<AppRouter>(() => createAppRouter(auth))

  const prevUserId = useRef<string | null>(auth.user?.uid ?? null)
  useEffect(() => {
    const currentUserId = auth.user?.uid ?? null
    // When the signed-in identity flips, re-run route guards.
    if (prevUserId.current !== currentUserId) {
      prevUserId.current = currentUserId
      void router.invalidate()
    }
  }, [auth.user?.uid, router])

  // Passing `context` here keeps the router's auth context in sync with React
  // state on every render (guards read `context.auth`).
  return <RouterProvider router={router} context={{ auth }} />
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <InnerApp />
        <Toaster />
      </AuthProvider>
    </ThemeProvider>
  )
}

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element #root not found')
}

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
